import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { Formik, Form, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/modules/userSlice";
import { DownloadFormHtmlToPdf, EpfForm } from "../components/api";
import useDraft from "../hooks/useDraft";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../drafts/draftHelpers";
import {
  FIELD_HINTS,
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
} from "../config/joineeFormConstants";
import JoineeFormShell, {
  JoineeFormFooterActions,
} from "../components/NewJoineForm/JoineeFormShell";
import {
  FormSectionTitle,
  FormTextField,
  FormSelectField,
  FormDateField,
  FormTwoCol,
  FormCheckboxField,
  FormRadioField,
  FormAddButton,
} from "../components/NewJoineForm/JoineeFormFields";
import ApprovedCustomizedDialogs from "../components/Model/ModelApproved";
import {
  canShowHrApprove,
  canShowHrResubmit,
  canShowHrReviewActions,
  isJoineeEmployeeFormLocked,
} from "../config/joineeConstants";
import ReSubmitCustomizedDialogs from "../components/Model/ModelReSubmit";
import LoadingButtons from "../components/Model/LoadingButtonModel";
import HrResubmitNotice from "../components/Joinee/HrResubmitNotice";
import {
  preferSavedDate,
  isFutureDate,
  ageInYearsFromDob,
} from "../utils/dateHelpers";

import { ruleSchema, validateDraft, notifyFormInvalid } from "../utils/inputRules";

var EMPTY_DISPLAY = "—";
var MAX_NOMINEES = 4;
var MAX_FAMILY = 6;
var EPF_RELATION_OPTIONS = [
  { value: "", label: "Select Relationship" },
  { value: "Mother", label: "Mother" },
  { value: "Father", label: "Father" },
  { value: "Spouse", label: "Spouse" },
  { value: "Daughter", label: "Daughter" },
  { value: "Son", label: "Son" },
  { value: "Sister", label: "Sister" },
  { value: "Brother", label: "Brother" },
  { value: "Mother-in-law", label: "Mother-in-law" },
  { value: "Father-in-law", label: "Father-in-law" },
  { value: "Guardian", label: "Guardian" },
  { value: "Other", label: "Other" },
];

var MINOR_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  return touched && error ? error : "";
}

function displayValue(value) {
  if (value === null || value === undefined) {
    return EMPTY_DISPLAY;
  }
  var text = String(value).trim();
  return text ? text : EMPTY_DISPLAY;
}

function formatDob(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : displayValue(value);
}

function formatMarital(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var v = String(value).toLowerCase();
  if (v === "married") return "Married";
  if (v === "single" || v === "unmarried") return "Unmarried";
  if (v === "widow") return "Widow";
  if (v === "widower") return "Widower";
  if (v === "divorcee") return "Divorcee";
  return displayValue(value);
}

function formatGender(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var v = String(value).toLowerCase();
  if (v === "male") return "Male";
  if (v === "female") return "Female";
  if (v === "prefer not to answer") return "Prefer not to answer";
  return displayValue(value);
}

function safeFatherName(userResp) {
  if (!userResp || !userResp.father) {
    return "";
  }
  if (typeof userResp.father === "string") {
    return userResp.father;
  }
  return userResp.father.name || "";
}

function mapAddress(addr) {
  var a = addr || {};
  return {
    addressLine1: a.addressLine1 || "",
    addressLine2: a.addressLine2 || "",
    addressLine3: a.addressLine3 || "",
    landmark: a.landmark || "",
    postalCode: a.postalCode || "",
    periodOfStay: a.periodOfStay || "",
    natureOfResidency: a.natureOfResidency || "",
  };
}

function emptyNominee() {
  return {
    name: "",
    address: "",
    epfdob: null,
    Reln: "",
    percentage: "",
    minor: "No",
  };
}

function emptyFamily() {
  return {
    familymember: "",
    address: "",
    epsdob: null,
    Reln: "",
    makemembernominee: false,
  };
}

function normalizeRelation(value) {
  if (!value) {
    return "";
  }
  var found = EPF_RELATION_OPTIONS.find(function (opt) {
    return (
      opt.value &&
      String(opt.value).toLowerCase() === String(value).toLowerCase()
    );
  });
  return found ? found.value : String(value);
}

function withRelationOption(value) {
  var normalized = normalizeRelation(value);
  if (!normalized) {
    return EPF_RELATION_OPTIONS;
  }
  var exists = EPF_RELATION_OPTIONS.some(function (opt) {
    return opt.value === normalized;
  });
  if (exists) {
    return EPF_RELATION_OPTIONS;
  }
  return EPF_RELATION_OPTIONS.concat([
    { value: normalized, label: normalized },
  ]);
}

function ReadOnlyRow(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: "16px", sm: "32px" },
        width: "100%",
        mb: 2,
      }}
    >
      {(props.items || []).map(function (item, index) {
        return (
          <Box
            key={item.label + "-" + index}
            sx={{
              display: "flex",
              flex: "1 1 0",
              gap: "16px",
              alignItems: "flex-start",
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#848F9A",
                lineHeight: 1.14,
                width: { xs: "140px", sm: "164px" },
                flexShrink: 0,
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#171A1C",
                lineHeight: 1.14,
                minWidth: 0,
                wordBreak: "break-word",
              }}
            >
              {item.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function LegalBox(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "stretch",
        width: "100%",
        minHeight: props.minHeight || 80,
        py: "10px",
        px: { xs: "12px", sm: "16px" },
        gap: "10px",
        bgcolor: "#F1F2F4",
        borderRadius: "4px",
        boxSizing: "border-box",
        mb: 3,
        mt: 1,
      }}
    >
      <Box
        component={props.asList ? "ul" : "div"}
        sx={{
          m: 0,
          width: "100%",
          pl: props.asList ? "20px" : 0,
          fontFamily: JOINEE_FORM_FONT,
          fontSize: 14,
          fontWeight: 400,
          color: "#2E3338",
          lineHeight: "22px",
          "& li": { mb: "6px" },
          "& li:last-child": { mb: 0 },
        }}
      >
        {props.children}
      </Box>
    </Box>
  );
}

function RemoveRowButton(props) {
  return (
    <Button
      type="button"
      variant="outlined"
      onClick={props.onClick}
      disabled={props.disabled}
      sx={{
        fontFamily: JOINEE_FORM_FONT,
        fontSize: 14,
        fontWeight: 400,
        textTransform: "none",
        color: JOINEE_FORM_COLORS.error,
        borderColor: JOINEE_FORM_COLORS.border,
        borderRadius: "6px",
        minHeight: 32,
      }}
    >
      Remove
    </Button>
  );
}

function epfNumberSchema(label) {
  return ruleSchema("pf", { label: label, required: true });
}

function buildValidationSchema() {
  return Yup.object({
    epf: epfNumberSchema("EPF Number"),
    eps: epfNumberSchema("EPS Number"),
    EPF: Yup.array()
      .of(
        Yup.object({
          name: ruleSchema("personName", {
            label: "Nominee name",
            required: true,
          }),
          address: ruleSchema("address", {
            label: "Nominee address",
            required: true,
          }),
          epfdob: Yup.mixed()
            .nullable()
            .required("Date of birth is required")
            .test(
              "not-future-dob",
              "Date of birth cannot be in the future",
              function (value) {
                return !isFutureDate(value);
              }
            ),
          Reln: Yup.string().trim().required("Relationship is required"),
          percentage: ruleSchema("percentShare", {
            label: "Share %",
            required: true,
          }),
          minor: Yup.string()
            .oneOf(["Yes", "No"], "Select Yes or No")
            .required("Select Yes or No")
            .test(
              "minor-matches-dob",
              "Minor must be Yes if age is under 18, otherwise No",
              function (value) {
                var dob = this.parent && this.parent.epfdob;
                var years = ageInYearsFromDob(dob);
                if (years === "" || years === null || typeof years === "undefined") {
                  return true;
                }
                var shouldBeMinor = Number(years) < 18;
                return shouldBeMinor ? value === "Yes" : value === "No";
              }
            ),
        })
      )
      .min(1, "Add at least one nominee")
      .max(MAX_NOMINEES, "Maximum " + MAX_NOMINEES + " nominees allowed")
      .test(
        "unique-nominee-names",
        "Nominee names must be unique",
        function (list) {
          if (!list || !list.length) return true;
          var names = list
            .map(function (row) {
              return String((row && row.name) || "")
                .trim()
                .toLowerCase();
            })
            .filter(Boolean);
          return names.length === new Set(names).size;
        }
      )
      .test("share-100", "Total percentage share must equal 100%", function (
        list
      ) {
        if (!list || !list.length) return false;
        var sum = list.reduce(function (acc, row) {
          return acc + Number((row && row.percentage) || 0);
        }, 0);
        return sum === 100;
      }),
    EPS: Yup.array()
      .of(
        Yup.object({
          familymember: ruleSchema("personName", {
            label: "Family member name",
            required: true,
          }),
          address: ruleSchema("address", {
            label: "Family member address",
            required: true,
          }),
          epsdob: Yup.mixed()
            .nullable()
            .required("Date of birth is required")
            .test(
              "not-future-eps-dob",
              "Date of birth cannot be in the future",
              function (value) {
                return !isFutureDate(value);
              }
            ),
          Reln: Yup.string().trim().required("Relationship is required"),
          makemembernominee: Yup.boolean(),
        })
      )
      .min(1, "Add at least one family member")
      .max(MAX_FAMILY, "Maximum " + MAX_FAMILY + " family members allowed")
      .test(
        "unique-family-names",
        "Family member names must be unique",
        function (list) {
          if (!list || !list.length) return true;
          var names = list
            .map(function (row) {
              return String((row && row.familymember) || "")
                .trim()
                .toLowerCase();
            })
            .filter(Boolean);
          return names.length === new Set(names).size;
        }
      )
      .test(
        "one-eps-nominee",
        "Select at least one family member as EPS nominee",
        function (list) {
          if (!list || !list.length) return false;
          return list.some(function (row) {
            return row && row.makemembernominee;
          });
        }
      ),
    epfLegalConsent: Yup.boolean().oneOf(
      [true],
      "Please confirm that the information provided is true and accurate"
    ),
  });
}

function syncEpsNomineeSnapshot(values) {
  var selected = (values.EPS || []).find(function (row) {
    return row && row.makemembernominee;
  });
  if (!selected) {
    return {
      nameofnominee: "",
      addofnominee: "",
      nomineedob: "",
      Reln: "",
    };
  }
  return {
    nameofnominee: selected.familymember || "",
    addofnominee: selected.address || "",
    nomineedob: selected.epsdob || "",
    Reln: selected.Reln || "",
  };
}

function Epf(props) {
  var navigate = useNavigate();
  var params = useParams();
  var routeId = params.id ? params.id : "";
  var redux = useSelector(selectUser);
  var user = redux && redux.user ? redux.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var userId = isAdmin ? props.id || routeId : user._id;

  var draftApi = useDraft("epf", "main");
  var loadDraft = draftApi.loadDraft;
  var saveDraft = draftApi.saveDraft;
  var deleteDraft = draftApi.deleteDraft;

  var [loading, setLoading] = useState(true);
  var [busy, setBusy] = useState(false);
  var [formKey, setFormKey] = useState(0);
  var [initialValues, setInitialValues] = useState(null);
  var [epfFlagButton, setEpfFlagButton] = useState("");
  var [approvedComment, setApprovedComment] = useState("");
  var [reSubmitComment, setReSubmitComment] = useState("");
  var [previewOpen, setPreviewOpen] = useState(false);
  var [previewHtml, setPreviewHtml] = useState("");
  var [previewLoading, setPreviewLoading] = useState(false);

  useEffect(
    function () {
      var isActive = true;
      if (!userId) {
        setLoading(false);
        return function () {
          isActive = false;
        };
      }

      setLoading(true);
      EpfForm.getEpfForm(userId)
        .then(function (res) {
          if (!isActive) {
            return;
          }
          var data = res && res.data ? res.data : {};
          var userResp = data.userResp || {};
          var epfDetails = data.epfDetails || {};
          var medical = data.medicalInsuranceResp || {};
          var sdf = data.selfDeclarationResp || {};
          var todayIso = new Date().toISOString();
          var place = sdf.place || "";

          var mappedEpfs =
            epfDetails.EPF && epfDetails.EPF.length
              ? epfDetails.EPF.map(function (e) {
                  var dob = e.epfdob || null;
                  var years = ageInYearsFromDob(dob);
                  var minorFromDob =
                    years === "" || years === null
                      ? null
                      : Number(years) < 18
                      ? "Yes"
                      : "No";
                  return {
                    name: e.name || "",
                    address: e.address || "",
                    epfdob: dob,
                    Reln: normalizeRelation(e.Reln),
                    percentage:
                      e.percentage === 0 || e.percentage
                        ? String(e.percentage)
                        : "",
                    minor:
                      minorFromDob ||
                      (e.minor === "No" || e.minor === "Yes" ? e.minor : "No"),
                  };
                })
              : [emptyNominee()];

          var mappedEps =
            epfDetails.EPS && epfDetails.EPS.length
              ? epfDetails.EPS.map(function (e) {
                  return {
                    familymember: e.familymember || "",
                    address: e.address || "",
                    epsdob: e.epsdob || null,
                    Reln: normalizeRelation(e.Reln),
                    makemembernominee: Boolean(e.makemembernominee),
                  };
                })
              : [emptyFamily()];

          var obj = {
            epfId: epfDetails._id || "",
            name: userResp.name || "",
            fatherName: safeFatherName(userResp),
            dob: medical.dob || userResp.dateOfBirth || "",
            pfAccount: userResp.pf || "",
            maritalStatus: userResp.maritalStatus || "",
            gender: userResp.gender || "",
            currentAddress: mapAddress(userResp.currentAddress),
            permanentAddress: mapAddress(userResp.permanentAddress),
            permanentAddressSame: Boolean(userResp.permanentAddressSame),
            doj: userResp.doj || "",
            epf:
              epfDetails.epf === 0 || epfDetails.epf
                ? String(epfDetails.epf)
                : "",
            eps:
              epfDetails.eps === 0 || epfDetails.eps
                ? String(epfDetails.eps)
                : "",
            EPF: mappedEpfs,
            epfauthorization: {
              nameofnominee: userResp.name || "",
              epfdate: preferSavedDate(
                epfDetails.epfauthorization &&
                  epfDetails.epfauthorization.epfdate,
                todayIso
              ),
              place: place,
            },
            EPS: mappedEps,
            nameofnominee: epfDetails.nameofnominee || "",
            addofnominee: epfDetails.addofnominee || "",
            nomineedob: epfDetails.nomineedob || "",
            Reln: normalizeRelation(epfDetails.Reln),
            epsauthorization: {
              name: userResp.name || "",
              epsdate: preferSavedDate(
                epfDetails.epsauthorization &&
                  epfDetails.epsauthorization.epsdate,
                todayIso
              ),
              place: place,
            },
            epfLegalConsent: Boolean(epfDetails.epfLegalConsent),
            epfLegalConsentAt: epfDetails.epfLegalConsentAt || null,
          };

          var snap = syncEpsNomineeSnapshot(obj);
          obj.nameofnominee = snap.nameofnominee || obj.nameofnominee;
          obj.addofnominee = snap.addofnominee || obj.addofnominee;
          obj.nomineedob = snap.nomineedob || obj.nomineedob;
          obj.Reln = snap.Reln || obj.Reln;

          setEpfFlagButton(userResp.isEPFFormComplete || "");
          setApprovedComment(
            userResp.comment && userResp.comment.approved
              ? userResp.comment.approved
              : ""
          );
          setReSubmitComment(
            userResp.comment && userResp.comment.resubmit
              ? userResp.comment.resubmit
              : ""
          );

          if (isAdmin) {
            setInitialValues(obj);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
            return;
          }

          return loadDraft().then(function (draft) {
            if (!isActive) {
              return;
            }
            var merged = applySectionDraftData("epf", "main", obj, draft);
            if (merged && merged.EPF && merged.EPF.length) {
              merged.EPF = merged.EPF.map(function (e) {
                var years = ageInYearsFromDob(e.epfdob);
                var minorFromDob =
                  years === "" || years === null
                    ? null
                    : Number(years) < 18
                    ? "Yes"
                    : "No";
                return Object.assign({}, emptyNominee(), e, {
                  Reln: normalizeRelation(e.Reln),
                  minor:
                    minorFromDob ||
                    (e.minor === "No" || e.minor === "Yes" ? e.minor : "No"),
                  percentage:
                    e.percentage === 0 || e.percentage
                      ? String(e.percentage)
                      : "",
                });
              });
            }
            if (merged && merged.EPS && merged.EPS.length) {
              merged.EPS = merged.EPS.map(function (e) {
                return Object.assign({}, emptyFamily(), e, {
                  Reln: normalizeRelation(e.Reln),
                  makemembernominee: Boolean(e.makemembernominee),
                });
              });
            }
            merged.epfauthorization = Object.assign(
              {},
              obj.epfauthorization,
              merged.epfauthorization || {},
              {
                nameofnominee: userResp.name || "",
                epfdate: preferSavedDate(
                  merged.epfauthorization && merged.epfauthorization.epfdate
                    ? merged.epfauthorization.epfdate
                    : epfDetails.epfauthorization &&
                        epfDetails.epfauthorization.epfdate,
                  todayIso
                ),
                place: place,
              }
            );
            merged.epsauthorization = Object.assign(
              {},
              obj.epsauthorization,
              merged.epsauthorization || {},
              {
                name: userResp.name || "",
                epsdate: preferSavedDate(
                  merged.epsauthorization && merged.epsauthorization.epsdate
                    ? merged.epsauthorization.epsdate
                    : epfDetails.epsauthorization &&
                        epfDetails.epsauthorization.epsdate,
                  todayIso
                ),
                place: place,
              }
            );
            var draftSnap = syncEpsNomineeSnapshot(merged);
            merged.nameofnominee = draftSnap.nameofnominee;
            merged.addofnominee = draftSnap.addofnominee;
            merged.nomineedob = draftSnap.nomineedob;
            merged.Reln = draftSnap.Reln;
            setInitialValues(merged);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
          });
        })
        .catch(function (err) {
          console.log(err);
          if (isActive) {
            setLoading(false);
            toast.error("Unable to load Nomination & Declaration Form");
          }
        });

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraft]
  );

  var fieldsReadOnly =
    isAdmin || isJoineeEmployeeFormLocked(epfFlagButton);
  var showCandidateActions =
    !isAdmin && !isJoineeEmployeeFormLocked(epfFlagButton);

  function payloadFromValues(values) {
    var accepted = Boolean(values.epfLegalConsent);
    var snap = syncEpsNomineeSnapshot(values);
    return {
      epf: String(values.epf || "").trim(),
      eps: String(values.eps || "").trim(),
      EPF: (values.EPF || []).map(function (row) {
        return {
          name: String(row.name || "").trim(),
          address: String(row.address || "").trim(),
          epfdob: row.epfdob || null,
          Reln: String(row.Reln || "").trim(),
          percentage: Number(row.percentage),
          minor: row.minor || "No",
        };
      }),
      epfauthorization: {
        nameofnominee: values.name || "",
        epfdate: preferSavedDate(
          values.epfauthorization && values.epfauthorization.epfdate,
          new Date().toISOString()
        ),
        place:
          values.epfauthorization && values.epfauthorization.place
            ? values.epfauthorization.place
            : "",
      },
      EPS: (values.EPS || []).map(function (row) {
        return {
          familymember: String(row.familymember || "").trim(),
          address: String(row.address || "").trim(),
          epsdob: row.epsdob || null,
          Reln: String(row.Reln || "").trim(),
          makemembernominee: Boolean(row.makemembernominee),
        };
      }),
      nameofnominee: snap.nameofnominee,
      addofnominee: snap.addofnominee,
      nomineedob: snap.nomineedob,
      Reln: snap.Reln,
      epsauthorization: {
        name: values.name || "",
        epsdate: preferSavedDate(
          values.epsauthorization && values.epsauthorization.epsdate,
          new Date().toISOString()
        ),
        place:
          values.epsauthorization && values.epsauthorization.place
            ? values.epsauthorization.place
            : "",
      },
      epfLegalConsent: accepted,
      epfLegalConsentAt: accepted
        ? values.epfLegalConsentAt || new Date().toISOString()
        : null,
    };
  }

  function persistEpf(values) {
    var payload = payloadFromValues(values);
    if (values.epfId) {
      return EpfForm.updateEpfForm(userId, payload).then(function (res) {
        return { res: res, epfId: values.epfId };
      });
    }
    return EpfForm.createEpfForm(userId, payload).then(function (res) {
      var newId =
        res && res.data && res.data._id ? res.data._id : values.epfId;
      return { res: res, epfId: newId };
    });
  }

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    return validateDraft(formik, buildValidationSchema()).then(function (ok) {
      return ok ? persistDraft(formik) : null;
    });
  }

  function persistDraft(formik) {
    var values = formik.values;
    var snap = syncEpsNomineeSnapshot(values);
    var wrapped = pickSectionDraftData(
      "epf",
      "main",
      Object.assign({}, values, snap)
    );
    setBusy("draft");
    return saveDraft(wrapped)
      .then(function (saved) {
        setBusy(false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    if (isAdmin) {
      if (formikHelpers && formikHelpers.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }
      return;
    }
    setBusy("next");
    persistEpf(values)
      .then(function (result) {
        if (result && result.epfId && !values.epfId) {
          formikHelpers.setFieldValue("epfId", result.epfId);
        }
        var snap = syncEpsNomineeSnapshot(values);
        formikHelpers.setFieldValue("nameofnominee", snap.nameofnominee);
        formikHelpers.setFieldValue("addofnominee", snap.addofnominee);
        formikHelpers.setFieldValue("nomineedob", snap.nomineedob);
        formikHelpers.setFieldValue("Reln", snap.Reln);
        if (
          values.epfLegalConsent &&
          !values.epfLegalConsentAt &&
          formikHelpers.setFieldValue
        ) {
          formikHelpers.setFieldValue(
            "epfLegalConsentAt",
            payloadFromValues(values).epfLegalConsentAt
          );
        }
        return deleteDraft().then(function () {
          return EpfForm.epfFormStatus(userId);
        });
      })
      .then(function () {
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        setEpfFlagButton("submitted");
        toast.success("Nomination & Declaration Form submitted");
        navigate("/");
      })
      .catch(function (err) {
        console.log(err);
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save Nomination & Declaration Form");
      });
  }

  function openPreview() {
    if (!userId) {
      return;
    }
    setPreviewLoading(true);
    DownloadFormHtmlToPdf.getform(userId, "EpfFormHtmlData")
      .then(function (res) {
        setPreviewLoading(false);
        if (res && res.success) {
          setPreviewHtml(res.data || "");
          setPreviewOpen(true);
        } else {
          toast.error("Unable to load preview");
        }
      })
      .catch(function () {
        setPreviewLoading(false);
        toast.error("Unable to load preview");
      });
  }

  function shareSum(list) {
    return (list || []).reduce(function (acc, row) {
      return acc + Number((row && row.percentage) || 0);
    }, 0);
  }

  var formApprovedProps = {
    userId: props.id || routeId,
    formState: "isEPFFormComplete",
    nextURL: "AdminForm11",
    approvedComment: approvedComment,
    setApprovedComment: setApprovedComment,
  };

  var formReSubmitProps = {
    userId: props.id || routeId,
    formState: "isEPFFormComplete",
    nextURL: "AdminForm11",
    reSubmitComment: reSubmitComment,
    setReSubmitComment: setReSubmitComment,
  };

  if (loading || !initialValues) {
    return (
      <Box sx={{ width: "100%", py: 6, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            color: JOINEE_FORM_COLORS.label,
          }}
        >
          Loading Nomination & Declaration Form…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      {isAdmin && !props.hideHrActions && canShowHrReviewActions(epfFlagButton) ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mb: 1.5,
            width: { xs: "100%", md: "82%" },
            maxWidth: 780,
            mx: "auto",
          }}
        >
          {canShowHrApprove(epfFlagButton) ? (
            <ApprovedCustomizedDialogs formApprovedProps={formApprovedProps} />
          ) : null}
          {canShowHrResubmit(epfFlagButton) ? (
            <ReSubmitCustomizedDialogs formReSubmitProps={formReSubmitProps} />
          ) : null}
        </Box>
      ) : null}

      <Formik
        key={formKey}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={buildValidationSchema()}
        onSubmit={handleSaveAndNext}
      >
        {function (formik) {
          var values = formik.values;
          var nomineeTotal = shareSum(values.EPF);
          var arrayErrors = formik.errors || {};

          return (
            <Form noValidate>
              <JoineeFormShell
                banner={
                  !isAdmin && epfFlagButton === "resubmission required" ? (
                    <HrResubmitNotice comment={reSubmitComment} embedded />
                  ) : null
                }
                hideSteps
                onBack={
                  !isAdmin
                    ? function () {
                        navigate("/");
                      }
                    : null
                }
                title="Nomination & Declaration Form"
                subtitle="Review and submit your nomination and declaration to complete your onboarding"
                headerAction={
                  isAdmin ? (
                    previewLoading ? (
                      <LoadingButtons />
                    ) : (
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={openPreview}
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 14,
                          fontWeight: 400,
                          textTransform: "none",
                          color: JOINEE_FORM_COLORS.primary,
                          borderColor: JOINEE_FORM_COLORS.primary,
                          borderRadius: "6px",
                          minHeight: 32,
                          px: "12px",
                          bgcolor: "#FDFDFD",
                        }}
                      >
                        Preview Form
                      </Button>
                    )
                  ) : null
                }
                footer={
                  showCandidateActions ? (
                    <JoineeFormFooterActions
                      showPrevious
                      onPrevious={function () {
                        navigate("/");
                      }}
                      onDraft={function () {
                        return handleSaveDraft(formik);
                      }}
                      onNext={function () {
                        formik.validateForm().then(function (errors) {
                          if (errors && Object.keys(errors).length) {
                            notifyFormInvalid(errors);
                            formik.setTouched(
                              {
                                epf: true,
                                eps: true,
                                epfLegalConsent: true,
                                EPF: (values.EPF || []).map(function () {
                                  return {
                                    name: true,
                                    address: true,
                                    epfdob: true,
                                    Reln: true,
                                    percentage: true,
                                    minor: true,
                                  };
                                }),
                                EPS: (values.EPS || []).map(function () {
                                  return {
                                    familymember: true,
                                    address: true,
                                    epsdob: true,
                                    Reln: true,
                                    makemembernominee: true,
                                  };
                                }),
                              },
                              true
                            );
                            return;
                          }
                          formik.handleSubmit();
                        });
                      }}
                      nextLabel="Save and next"
                      disabled={Boolean(busy)}
                      drafting={busy === "draft"}
                      saving={busy === "next"}
                    />
                  ) : null
                }
              >
                <FormSectionTitle>
                  For exempted/unexempted establishments
                </FormSectionTitle>
                <LegalBox>
                  Declaration and Nomination Form Under the Employee&apos;s
                  Provident Funds &amp; Employees&apos; Pension Scheme
                  (Paragraph 33 &amp; 61 (1) of the Employees&apos; Provident
                  Fund Scheme, 1952 &amp; Paragraph 18 of the Employees&apos;
                  Pension Scheme, 1995)
                </LegalBox>

                <ReadOnlyRow
                  items={[
                    { label: "Full Name:", value: displayValue(values.name) },
                    {
                      label: "Father's / Husband's Name:",
                      value: displayValue(values.fatherName),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Date of Birth:",
                      value: formatDob(values.dob),
                    },
                    {
                      label: "PF Account Number:",
                      value: displayValue(values.pfAccount),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Marital Status:",
                      value: formatMarital(values.maritalStatus),
                    },
                    {
                      label: "Gender:",
                      value: formatGender(values.gender),
                    },
                  ]}
                />

                <FormSectionTitle>Current Address</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Address Line 1:",
                      value: displayValue(values.currentAddress.addressLine1),
                    },
                    {
                      label: "Address Line 2:",
                      value: displayValue(values.currentAddress.addressLine2),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Landmark:",
                      value: displayValue(values.currentAddress.landmark),
                    },
                    {
                      label: "Postal Code:",
                      value: displayValue(values.currentAddress.postalCode),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Period of Stay:",
                      value: displayValue(values.currentAddress.periodOfStay),
                    },
                    {
                      label: "Nature of Residency:",
                      value: displayValue(
                        values.currentAddress.natureOfResidency
                      ),
                    },
                  ]}
                />

                <FormSectionTitle>Permanent Address</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Address Line 1:",
                      value: displayValue(
                        values.permanentAddress.addressLine1
                      ),
                    },
                    {
                      label: "Address Line 2:",
                      value: displayValue(
                        values.permanentAddress.addressLine2
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Landmark:",
                      value: displayValue(values.permanentAddress.landmark),
                    },
                    {
                      label: "Postal Code:",
                      value: displayValue(values.permanentAddress.postalCode),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Period of Stay:",
                      value: displayValue(
                        values.permanentAddress.periodOfStay
                      ),
                    },
                    {
                      label: "Nature of Residency:",
                      value: displayValue(
                        values.permanentAddress.natureOfResidency
                      ),
                    },
                  ]}
                />

                <Box sx={{ mt: 3 }}>
                  <FormSectionTitle>EPF &amp; EPS Details</FormSectionTitle>
                </Box>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Date of Joining:",
                      value: formatDob(values.doj),
                    },
                  ]}
                />
                <FormTwoCol>
                  <FormTextField
                    id="epf-number"
                    rule="pf"
                    name="epf"
                    label="EPF Number"
                    hint={FIELD_HINTS.pf}
                    required
                    value={values.epf}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "epf")}
                    disabled={fieldsReadOnly}
                    inputProps={{ autoComplete: "off" }}
                  />
                  <FormTextField
                    id="eps-number"
                    rule="pf"
                    name="eps"
                    label="EPS Number"
                    hint="Usually the same as your PF member ID, e.g. MHBAN00000640000000125."
                    required
                    value={values.eps}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "eps")}
                    disabled={fieldsReadOnly}
                    inputProps={{ autoComplete: "off" }}
                  />
                </FormTwoCol>

                <FieldArray name="EPF">
                  {function (arrayHelpers) {
                    var canAddNominee =
                      !fieldsReadOnly &&
                      (values.EPF || []).length < MAX_NOMINEES;
                    return (
                      <Box sx={{ mt: 3 }}>
                        <FormSectionTitle
                          action={
                            !fieldsReadOnly ? (
                              <FormAddButton
                                disabled={!canAddNominee}
                                onClick={function () {
                                  if (!canAddNominee) {
                                    return;
                                  }
                                  arrayHelpers.push(emptyNominee());
                                }}
                              >
                                + Add Nominee
                              </FormAddButton>
                            ) : null
                          }
                        >
                          PART-A (EPF)
                        </FormSectionTitle>
                        <LegalBox>
                          I hereby nominate the person(s) / cancel the
                          nomination made by me previously and person(s)
                          mentioned below to receive the amount standing to my
                          credit in the Employees&apos; Provident Fund, in the
                          event of my death.
                        </LegalBox>

                        {(values.EPF || []).map(function (row, index) {
                          return (
                            <Box key={"epf-" + index} sx={{ mb: 3.5 }}>
                              {index > 0 ? (
                                <FormSectionTitle
                                  action={
                                    !fieldsReadOnly ? (
                                      <RemoveRowButton
                                        onClick={function () {
                                          arrayHelpers.remove(index);
                                        }}
                                      />
                                    ) : null
                                  }
                                >
                                  Nominee {index + 1}
                                </FormSectionTitle>
                              ) : null}
                              <FormTwoCol>
                                <FormTextField
                                  id={"epf-nominee-name-" + index}
                                  rule="personName"
                                  name={"EPF." + index + ".name"}
                                  label="Name of the Nominee"
                                  placeholder="Enter Name of the Nominee"
                                  required
                                  value={row.name}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "EPF." + index + ".name"
                                  )}
                                  disabled={fieldsReadOnly}
                                  inputProps={{ autoComplete: "off" }}
                                />
                                <FormTextField
                                  id={"epf-nominee-address-" + index}
                                  rule="address"
                                  name={"EPF." + index + ".address"}
                                  label="Address of The Nominee"
                                  placeholder="Enter Address of The Nominee"
                                  required
                                  value={row.address}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "EPF." + index + ".address"
                                  )}
                                  disabled={fieldsReadOnly}
                                  inputProps={{ autoComplete: "off" }}
                                />
                              </FormTwoCol>
                              <FormTwoCol>
                                <FormSelectField
                                  id={"epf-nominee-reln-" + index}
                                  name={"EPF." + index + ".Reln"}
                                  label="Nominee's Relationship with The Member"
                                  placeholder="Select Relationship"
                                  required
                                  value={row.Reln}
                                  options={withRelationOption(row.Reln)}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "EPF." + index + ".Reln"
                                  )}
                                  disabled={fieldsReadOnly}
                                />
                                <FormTextField
                                  id={"epf-nominee-percentage-" + index}
                                  rule="percentShare"
                                  name={"EPF." + index + ".percentage"}
                                  label="Percentage of Share in PF Paid to Nominee"
                                  placeholder="e.g. 50"
                                  required
                                  value={row.percentage}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "EPF." + index + ".percentage"
                                  )}
                                  disabled={fieldsReadOnly}
                                  inputProps={{ autoComplete: "off" }}
                                />
                              </FormTwoCol>
                              <FormTwoCol>
                                <FormDateField
                                  id={"epf-nominee-dob-" + index}
                                  name={"EPF." + index + ".epfdob"}
                                  label="Date of Birth"
                                  placeholder="Select Date of Birth"
                                  required
                                  disableFuture
                                  value={row.epfdob}
                                  onChange={function (iso) {
                                    formik.setFieldValue(
                                      "EPF." + index + ".epfdob",
                                      iso
                                    );
                                    var years = ageInYearsFromDob(iso);
                                    if (
                                      years !== "" &&
                                      years !== null &&
                                      typeof years !== "undefined"
                                    ) {
                                      formik.setFieldValue(
                                        "EPF." + index + ".minor",
                                        Number(years) < 18 ? "Yes" : "No"
                                      );
                                    }
                                  }}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "EPF." + index + ".epfdob"
                                  )}
                                  disabled={fieldsReadOnly}
                                />
                                <FormRadioField
                                  name={"EPF." + index + ".minor"}
                                  label="Is Nominee Minor"
                                  required
                                  value={row.minor}
                                  options={MINOR_OPTIONS}
                                  onChange={function (e) {
                                    formik.setFieldValue(
                                      "EPF." + index + ".minor",
                                      e.target.value
                                    );
                                  }}
                                  error={errMsg(
                                    formik,
                                    "EPF." + index + ".minor"
                                  )}
                                  disabled={fieldsReadOnly}
                                />
                              </FormTwoCol>
                            </Box>
                          );
                        })}

                        {typeof arrayErrors.EPF === "string" ? (
                          <Typography
                            sx={{
                              fontFamily: JOINEE_FORM_FONT,
                              fontSize: 12,
                              color: JOINEE_FORM_COLORS.error,
                              mb: 1,
                            }}
                          >
                            {arrayErrors.EPF}
                          </Typography>
                        ) : null}

                        <Typography
                          sx={{
                            fontFamily: JOINEE_FORM_FONT,
                            fontSize: 13,
                            color:
                              nomineeTotal === 100 ? "#1B7A4E" : "#848F9A",
                            mb: 1.5,
                          }}
                        >
                          Total share: {nomineeTotal}%
                          {nomineeTotal !== 100
                            ? " (must equal 100%)"
                            : ""}
                        </Typography>
                      </Box>
                    );
                  }}
                </FieldArray>

                <FormSectionTitle>Authorization</FormSectionTitle>
                <LegalBox asList minHeight={120}>
                  <li>
                    Certified that I have no family as defined in para 2 (g) of
                    the Employee&apos;s Provident Fund Scheme 1952 and should I
                    acquire a family hereafter the above nomination should be
                    deemed as cancelled
                  </li>
                  <li>
                    Certified that my father / mother is/are depended upon me.
                  </li>
                  <li>
                    Unmarried members in the absence of dependent parents may
                    nominate any other person to receive the shares
                  </li>
                </LegalBox>

                <ReadOnlyRow
                  items={[
                    {
                      label: "Full Name:",
                      value: displayValue(
                        values.epfauthorization
                          ? values.epfauthorization.nameofnominee
                          : values.name
                      ),
                    },
                    {
                      label: "Place:",
                      value: displayValue(
                        values.epfauthorization
                          ? values.epfauthorization.place
                          : ""
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Today’s Date:",
                      value: formatDob(
                        values.epfauthorization
                          ? values.epfauthorization.epfdate
                          : null
                      ),
                    },
                  ]}
                />

                <LegalBox>
                  Note : A Fresh nomination shall be made by the member on
                  his/her marriage and any nomination made before such marriage
                  shall be deemed to be invalid
                </LegalBox>

                <FieldArray name="EPS">
                  {function (arrayHelpers) {
                    var canAddFamily =
                      !fieldsReadOnly &&
                      (values.EPS || []).length < MAX_FAMILY;
                    return (
                      <Box sx={{ mt: 3 }}>
                        <FormSectionTitle
                          action={
                            !fieldsReadOnly ? (
                              <FormAddButton
                                disabled={!canAddFamily}
                                onClick={function () {
                                  if (!canAddFamily) {
                                    return;
                                  }
                                  arrayHelpers.push(emptyFamily());
                                }}
                              >
                                + Add Family Member
                              </FormAddButton>
                            ) : null
                          }
                        >
                          PART-B (EPS)
                        </FormSectionTitle>
                        <LegalBox>
                          I hereby furnish below particulars of the members of
                          my family who would be eligible to receive
                          widow/children pension in the event of my death.
                        </LegalBox>

                        {(values.EPS || []).map(function (row, index) {
                          return (
                            <Box key={"eps-" + index} sx={{ mb: 3.5 }}>
                              {index > 0 ? (
                                <FormSectionTitle
                                  action={
                                    !fieldsReadOnly ? (
                                      <RemoveRowButton
                                        onClick={function () {
                                          arrayHelpers.remove(index);
                                        }}
                                      />
                                    ) : null
                                  }
                                >
                                  Family Member {index + 1}
                                </FormSectionTitle>
                              ) : null}
                              <FormTwoCol>
                                <FormTextField
                                  id={"eps-family-name-" + index}
                                  rule="personName"
                                  name={"EPS." + index + ".familymember"}
                                  label="Name of the Family Member"
                                  placeholder="Enter Name of the Family Member"
                                  required
                                  value={row.familymember}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "EPS." + index + ".familymember"
                                  )}
                                  disabled={fieldsReadOnly}
                                  inputProps={{ autoComplete: "off" }}
                                />
                                <FormTextField
                                  id={"eps-family-address-" + index}
                                  rule="address"
                                  name={"EPS." + index + ".address"}
                                  label="Address of The Family Member"
                                  placeholder="Enter Address of The Family Member"
                                  required
                                  value={row.address}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "EPS." + index + ".address"
                                  )}
                                  disabled={fieldsReadOnly}
                                  inputProps={{ autoComplete: "off" }}
                                />
                              </FormTwoCol>
                              <FormTwoCol>
                                <FormDateField
                                  id={"eps-family-dob-" + index}
                                  name={"EPS." + index + ".epsdob"}
                                  label="Date of Birth"
                                  placeholder="Select Date of Birth"
                                  required
                                  disableFuture
                                  value={row.epsdob}
                                  onChange={function (iso) {
                                    formik.setFieldValue(
                                      "EPS." + index + ".epsdob",
                                      iso
                                    );
                                  }}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "EPS." + index + ".epsdob"
                                  )}
                                  disabled={fieldsReadOnly}
                                />
                                <FormSelectField
                                  id={"eps-family-reln-" + index}
                                  name={"EPS." + index + ".Reln"}
                                  label="Relationship"
                                  placeholder="Select Relationship"
                                  required
                                  value={row.Reln}
                                  options={withRelationOption(row.Reln)}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={errMsg(
                                    formik,
                                    "EPS." + index + ".Reln"
                                  )}
                                  disabled={fieldsReadOnly}
                                />
                              </FormTwoCol>
                              <Box sx={{ mt: 0.5, mb: 0.5 }}>
                                <FormCheckboxField
                                  name={
                                    "EPS." + index + ".makemembernominee"
                                  }
                                  checked={Boolean(row.makemembernominee)}
                                  disabled={fieldsReadOnly}
                                  onChange={function (e) {
                                    var checked = Boolean(e.target.checked);
                                    var next = (values.EPS || []).map(
                                      function (item, i) {
                                        return Object.assign({}, item, {
                                          makemembernominee:
                                            i === index ? checked : false,
                                        });
                                      }
                                    );
                                    formik.setFieldValue("EPS", next);
                                    var snap = syncEpsNomineeSnapshot({
                                      EPS: next,
                                    });
                                    formik.setFieldValue(
                                      "nameofnominee",
                                      snap.nameofnominee
                                    );
                                    formik.setFieldValue(
                                      "addofnominee",
                                      snap.addofnominee
                                    );
                                    formik.setFieldValue(
                                      "nomineedob",
                                      snap.nomineedob
                                    );
                                    formik.setFieldValue("Reln", snap.Reln);
                                  }}
                                  label="Make this member a nominee"
                                />
                              </Box>
                            </Box>
                          );
                        })}

                        {typeof arrayErrors.EPS === "string" ? (
                          <Typography
                            sx={{
                              fontFamily: JOINEE_FORM_FONT,
                              fontSize: 12,
                              color: JOINEE_FORM_COLORS.error,
                              mb: 1,
                            }}
                          >
                            {arrayErrors.EPS}
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  }}
                </FieldArray>

                <LegalBox asList minHeight={100}>
                  <li>
                    Certified that I have no family as defined in para 2 (vii)
                    of the Employee&apos;s Pension Scheme 1995 and should I
                    acquire a family hereafter the above nomination should be
                    deemed as cancelled
                  </li>
                  <li>
                    I hereby nominate the following person for receiving the
                    monthly widow pension (admissible under para 16(2) (g) (I)
                    &amp; (ii) in the event of my death without leaving any
                    eligible family member for receiving pension.
                  </li>
                </LegalBox>

                <ReadOnlyRow
                  items={[
                    {
                      label: "Name of the Nominee:",
                      value: displayValue(values.nameofnominee),
                    },
                    {
                      label: "Address of the Nominee:",
                      value: displayValue(values.addofnominee),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Date of Birth:",
                      value: formatDob(values.nomineedob),
                    },
                    {
                      label: "Relationship:",
                      value: displayValue(values.Reln),
                    },
                  ]}
                />

                <FormSectionTitle>PART-B Authorization</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Full Name:",
                      value: displayValue(
                        values.epsauthorization
                          ? values.epsauthorization.name
                          : values.name
                      ),
                    },
                    {
                      label: "Place:",
                      value: displayValue(
                        values.epsauthorization
                          ? values.epsauthorization.place
                          : ""
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Today’s Date:",
                      value: formatDob(
                        values.epsauthorization
                          ? values.epsauthorization.epsdate
                          : null
                      ),
                    },
                  ]}
                />

                {!isAdmin ? (
                  <Box
                    sx={{ mt: 1, mb: 1 }}
                    data-ack-error={
                      errMsg(formik, "epfLegalConsent") ? "true" : undefined
                    }
                  >
                    <FormCheckboxField
                      name="epfLegalConsent"
                      checked={Boolean(values.epfLegalConsent)}
                      onChange={function (e) {
                        var checked = Boolean(e.target.checked);
                        formik.setFieldValue("epfLegalConsent", checked, true);
                        formik.setFieldValue(
                          "epfLegalConsentAt",
                          checked
                            ? values.epfLegalConsentAt ||
                                new Date().toISOString()
                            : null,
                          false
                        );
                        formik.setFieldTouched(
                          "epfLegalConsent",
                          true,
                          false
                        );
                        if (checked) {
                          formik.setFieldError("epfLegalConsent", undefined);
                        }
                      }}
                      label="I confirm that the nomination and declaration details provided above are true and accurate to the best of my knowledge."
                      disabled={fieldsReadOnly}
                      legal
                    />
                    {errMsg(formik, "epfLegalConsent") &&
                    !values.epfLegalConsent ? (
                      <Typography
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 12,
                          fontWeight: 400,
                          color: JOINEE_FORM_COLORS.error,
                          mt: 0.5,
                          ml: "28px",
                        }}
                      >
                        {errMsg(formik, "epfLegalConsent")}
                      </Typography>
                    ) : null}
                    <Box
                      sx={{
                        borderBottom:
                          "1px solid " + JOINEE_FORM_COLORS.border,
                        mt: 2,
                        mb: 1,
                      }}
                    />
                  </Box>
                ) : null}
              </JoineeFormShell>
            </Form>
          );
        }}
      </Formik>

      <Dialog
        open={previewOpen}
        onClose={function () {
          setPreviewOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontWeight: 600,
            pr: 6,
          }}
        >
          Preview Form
          <IconButton
            aria-label="close"
            onClick={function () {
              setPreviewOpen(false);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{ width: "100%", overflowX: "auto" }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Epf;
